import { LoraStatusEnum } from "@/enums/lora-status.enum.ts";
import { usePlaceholderHook } from "@/hooks/use-placeholder.hook.ts";
import { AnglesUpDown } from "@/icons/AnglesUpDown";
import { PenToSquare } from "@/icons/PenToSquare.tsx";
import { TrashCan } from "@/icons/TrashCan.tsx";
import api from "@/lib/api.ts";
import { loraStatusColorMap } from "@/lib/helpers.ts";
import { LoraModel } from "@/models/lora.model.ts";
import {
  ActionIcon,
  Badge,
  Card,
  Image,
  LoadingOverlay,
  Menu,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router";

type LoraCardProps = {
  lora: LoraModel;
  onEdit: (lora: LoraModel) => void;
  onDelete: (lora: LoraModel) => void;
};

export function LoraCard({ lora, onEdit, onDelete }: LoraCardProps) {
  const navigate = useNavigate();

  const onClick = () => {
    if (lora.status === LoraStatusEnum.published) {
      return navigate(`/playground?character=${lora.uuid}`);
    }

    navigate(`/characters/${lora.uuid}/uploader`);
  };

  const mutation = useMutation({
    mutationFn: () => {
      return api.patch(`loras/${lora.id}`, {
        status:
          lora.status === LoraStatusEnum.published
            ? LoraStatusEnum.unpublished
            : LoraStatusEnum.published,
      });
    },
    onSuccess: () => {
      notifications.show({
        title: "Success!",
        message: "The status of the character has been successfully updated.",
        color: "green",
      });
    },
    retry: 1,
  });

  const placeholder = usePlaceholderHook();
  const cover = lora.cover || placeholder;

  const memoizedEditTooltip = useMemo(
    () => (
      <Tooltip label="Edit model" withArrow>
        <ActionIcon
          onClick={() => onEdit(lora)}
          variant="outline"
          color="blue"
          aria-label="Edit"
        >
          <PenToSquare size={12} />
        </ActionIcon>
      </Tooltip>
    ),
    [onEdit, lora],
  );

  const memoizedDeleteTooltip = useMemo(
    () => (
      <Tooltip label="Delete model" withArrow>
        <ActionIcon
          onClick={() => onDelete(lora)}
          variant="outline"
          color="red"
          aria-label="Delete"
        >
          <TrashCan size={12} />
        </ActionIcon>
      </Tooltip>
    ),
    [onDelete, lora],
  );

  return (
    <Card withBorder>
      <Card.Section>
        <LoadingOverlay
          visible={lora.status === LoraStatusEnum.training}
          loaderProps={{ color: "blue", type: "bars" }}
          zIndex={5}
          overlayProps={{ radius: "sm", blur: 1 }}
        />

        <Image
          className="cursor-pointer"
          onClick={onClick}
          fallbackSrc={cover}
          alt={lora.name}
          height={300}
          width={300}
        />

        {[LoraStatusEnum.published, LoraStatusEnum.unpublished].includes(
          lora.status,
        ) && (
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <Badge
                style={{ cursor: "pointer" }}
                className="absolute top-2 right-2 z-[6]"
                color={loraStatusColorMap[lora.status]}
                radius="md"
                rightSection={
                  <AnglesUpDown size={10} style={{ color: "white" }} />
                }
              >
                {lora.status}
              </Badge>
            </Menu.Target>

            <Menu.Dropdown>
              {lora.status === LoraStatusEnum.published && (
                <Menu.Item onClick={() => mutation.mutate()}>
                  Unpublish
                </Menu.Item>
              )}
              {lora.status === LoraStatusEnum.unpublished && (
                <Menu.Item onClick={() => mutation.mutate()}>Publish</Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        )}

        {![LoraStatusEnum.published, LoraStatusEnum.unpublished].includes(
          lora.status,
        ) && (
          <Badge
            color={loraStatusColorMap[lora.status]}
            radius="md"
            className="absolute top-2 right-2 z-[6]"
          >
            {lora.status}
          </Badge>
        )}

        <Badge
          color="dark"
          radius="md"
          className="absolute bottom-2 left-2 z-[6]"
        >
          {lora.name}
        </Badge>
        <div className="flex gap-2 absolute bottom-2 right-2">
          {memoizedEditTooltip}
          {memoizedDeleteTooltip}
        </div>
      </Card.Section>
    </Card>
  );
}
